import { useState, useEffect } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { 
  getIncome, createIncome, deleteIncome, 
  getIncomeCategories, createIncomeCategory, deleteIncomeCategory,
  getERPClients 
} from '@/lib/api';
import { toast } from 'sonner';
import { 
  Plus, Trash2, Loader2, TrendingUp, Tag, Calendar 
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

export default function ERPIncome() {
  const [income, setIncome] = useState([]);
  const [categories, setCategories] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [categoryDialogOpen, setCategoryDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [newCategory, setNewCategory] = useState('');
  
  const [formData, setFormData] = useState({
    amount: '',
    category_id: '',
    client_id: '',
    description: '',
    date: new Date().toISOString().split('T')[0],
    payment_method: 'bank_transfer',
    reference: '',
  });

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [incomeRes, categoriesRes, clientsRes] = await Promise.all([
        getIncome(String(selectedMonth).padStart(2, '0'), String(selectedYear)),
        getIncomeCategories(),
        getERPClients(false),
      ]);
      setIncome(incomeRes.data);
      setCategories(categoriesRes.data);
      setClients(clientsRes.data);
    } catch (err) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.amount || !formData.category_id || !formData.description) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      await createIncome({
        ...formData,
        amount: parseFloat(formData.amount),
      });
      toast.success('Income recorded');
      loadData();
      setDialogOpen(false);
      setFormData({
        amount: '',
        category_id: '',
        client_id: '',
        description: '',
        date: new Date().toISOString().split('T')[0],
        payment_method: 'bank_transfer',
        reference: '',
      });
    } catch (err) {
      toast.error('Failed to record income');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this income record?')) return;
    try {
      await deleteIncome(id);
      toast.success('Income deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete income');
    }
  };

  const handleAddCategory = async () => {
    if (!newCategory.trim()) return;
    try {
      await createIncomeCategory({ name: newCategory.trim() });
      toast.success('Category added');
      setNewCategory('');
      loadData();
    } catch (err) {
      toast.error('Failed to add category');
    }
  };

  const handleDeleteCategory = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try {
      await deleteIncomeCategory(id);
      toast.success('Category deleted');
      loadData();
    } catch (err) {
      toast.error('Failed to delete category');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  const totalIncome = income.reduce((sum, i) => sum + i.amount, 0);

  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const paymentMethods = [
    { value: 'bank_transfer', label: 'Bank Transfer' },
    { value: 'cash', label: 'Cash' },
    { value: 'upi', label: 'UPI' },
    { value: 'cheque', label: 'Cheque' },
    { value: 'card', label: 'Card' },
    { value: 'other', label: 'Other' },
  ];

  return (
    <AdminLayout title="ERP - Income">
      <div className="space-y-6" data-testid="erp-income">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="font-syne text-2xl font-bold text-white">Income Tracking</h1>
            <p className="text-[#888] text-sm">Record and manage all income sources</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setCategoryDialogOpen(true)}
              data-testid="manage-categories-btn"
              className="flex items-center gap-2 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5 transition-colors"
            >
              <Tag className="w-4 h-4" />
              Categories
            </button>
            <button
              onClick={() => setDialogOpen(true)}
              data-testid="add-income-btn"
              className="flex items-center gap-2 bg-green-600 text-white px-5 py-3 text-sm font-semibold hover:bg-green-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Income
            </button>
          </div>
        </div>

        {/* Summary and Filters */}
        <div className="grid md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-[#0A0A0A] border border-[#1F1F1F] p-6">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 text-green-500" />
              </div>
              <div>
                <p className="text-[#888] text-sm">Total Income ({months[selectedMonth - 1]} {selectedYear})</p>
                <p className="font-syne text-3xl font-bold text-green-500">{formatCurrency(totalIncome)}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] p-6">
            <p className="text-[#888] text-sm mb-3">Filter by Period</p>
            <div className="flex gap-2">
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(parseInt(e.target.value))}
                className="flex-1 bg-[#121212] border border-[#333] text-white px-3 py-2 text-sm outline-none"
              >
                {months.map((month, idx) => (
                  <option key={idx} value={idx + 1}>{month}</option>
                ))}
              </select>
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                className="bg-[#121212] border border-[#333] text-white px-3 py-2 text-sm outline-none"
              >
                {[2024, 2025, 2026, 2027].map((year) => (
                  <option key={year} value={year}>{year}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Income List */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-green-500 animate-spin" />
          </div>
        ) : income.length === 0 ? (
          <div className="text-center py-16 bg-[#0A0A0A] border border-[#1F1F1F]">
            <TrendingUp className="w-12 h-12 text-[#333] mx-auto mb-4" />
            <p className="text-[#555]">No income recorded for this period</p>
            <button
              onClick={() => setDialogOpen(true)}
              className="mt-4 text-green-500 text-sm hover:underline"
            >
              Record your first income
            </button>
          </div>
        ) : (
          <div className="bg-[#0A0A0A] border border-[#1F1F1F] overflow-hidden">
            <table className="w-full">
              <thead className="bg-[#121212]">
                <tr className="text-left text-[#888] text-sm">
                  <th className="px-6 py-4">Date</th>
                  <th className="px-6 py-4">Description</th>
                  <th className="px-6 py-4">Category</th>
                  <th className="px-6 py-4">Client</th>
                  <th className="px-6 py-4 text-right">Amount</th>
                  <th className="px-6 py-4 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {income.map((item) => (
                  <tr 
                    key={item.id} 
                    data-testid={`income-row-${item.id}`}
                    className="border-t border-[#1F1F1F] hover:bg-[#121212]"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-white text-sm">
                        <Calendar className="w-4 h-4 text-[#555]" />
                        {item.date}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-white text-sm">{item.description}</td>
                    <td className="px-6 py-4">
                      <span className="text-xs px-2 py-1 bg-green-500/10 text-green-500">
                        {item.category_name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-[#888] text-sm">{item.client_name || '-'}</td>
                    <td className="px-6 py-4 text-right font-syne font-bold text-green-500">
                      {formatCurrency(item.amount)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => handleDelete(item.id)}
                        data-testid={`delete-income-${item.id}`}
                        className="text-[#555] hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Add Income Dialog */}
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-syne text-xl">Record Income</DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Amount (INR) *</label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                    data-testid="income-amount-input"
                    className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                    placeholder="0"
                    min="0"
                    step="0.01"
                  />
                </div>
                <div>
                  <label className="block text-[#888] text-sm mb-2">Date *</label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    data-testid="income-date-input"
                    className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block text-[#888] text-sm mb-2">Category *</label>
                <select
                  value={formData.category_id}
                  onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                  data-testid="income-category-select"
                  className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat.id} value={cat.id}>{cat.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[#888] text-sm mb-2">Client (Optional)</label>
                <select
                  value={formData.client_id}
                  onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                  data-testid="income-client-select"
                  className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                >
                  <option value="">Select client</option>
                  {clients.map((client) => (
                    <option key={client.id} value={client.id}>{client.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-[#888] text-sm mb-2">Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  data-testid="income-description-input"
                  className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                  placeholder="Payment description"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[#888] text-sm mb-2">Payment Method</label>
                  <select
                    value={formData.payment_method}
                    onChange={(e) => setFormData({ ...formData, payment_method: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                  >
                    {paymentMethods.map((pm) => (
                      <option key={pm.value} value={pm.value}>{pm.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-[#888] text-sm mb-2">Reference</label>
                  <input
                    type="text"
                    value={formData.reference}
                    onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                    className="w-full bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                    placeholder="Invoice/Transaction ID"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setDialogOpen(false)}
                  className="flex-1 border border-[#333] text-white px-4 py-3 text-sm font-semibold hover:bg-white/5"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  data-testid="save-income-btn"
                  className="flex-1 bg-green-600 text-white px-4 py-3 text-sm font-semibold hover:bg-green-700 disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Record Income'}
                </button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Categories Dialog */}
        <Dialog open={categoryDialogOpen} onOpenChange={setCategoryDialogOpen}>
          <DialogContent className="bg-[#0A0A0A] border-[#1F1F1F] text-white max-w-md">
            <DialogHeader>
              <DialogTitle className="font-syne text-xl">Income Categories</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 mt-4">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  data-testid="new-income-category-input"
                  className="flex-1 bg-[#121212] border border-[#333] focus:border-green-500 text-white px-4 py-3 text-sm outline-none"
                  placeholder="New category name"
                />
                <button
                  onClick={handleAddCategory}
                  data-testid="add-income-category-btn"
                  className="bg-green-600 text-white px-4 py-3 text-sm font-semibold hover:bg-green-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>

              <div className="space-y-2 max-h-64 overflow-y-auto">
                {categories.length === 0 ? (
                  <p className="text-[#555] text-center py-4">No categories yet</p>
                ) : (
                  categories.map((cat) => (
                    <div 
                      key={cat.id}
                      className="flex items-center justify-between p-3 bg-[#121212] border border-[#1F1F1F]"
                    >
                      <span className="text-white text-sm">{cat.name}</span>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="text-[#555] hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
